import { userStore } from '@/stores/user.store';
import { Routes } from '@/types/routes';

/**
 * Welcome page visibility and post-auth landing.
 *
 * Every input comes from the user payload, so these decisions work before the
 * client PostHog SDK has loaded:
 * - `posthog_feature_flags`: PostHog flags evaluated server-side
 * - `is_welcome_page_eligible`: fewer than 5 sent chat messages
 * - `feature_flags.welcome_page_dismissed`: the user's dismissal preference
 */

/** Keys into the server-evaluated `posthog_feature_flags` map. */
const WELCOME_PAGE_POSTHOG_FLAG = 'welcome-page';
const WELCOME_PAGE_DEFAULT_VIEW_POSTHOG_FLAG = 'welcome-page-default-view';

function isWelcomePageEnabledByPostHog(): boolean {
  // A served variant means enabled; a missing key means the flag was off or
  // not evaluated.
  const postHogFlags = userStore.user?.posthog_feature_flags ?? {};
  return typeof postHogFlags[WELCOME_PAGE_POSTHOG_FLAG] === 'string';
}

function isDefaultViewEnabledByPostHog(): boolean {
  const postHogFlags = userStore.user?.posthog_feature_flags ?? {};
  return postHogFlags[WELCOME_PAGE_DEFAULT_VIEW_POSTHOG_FLAG] === true;
}

function isUserEligibleForWelcomePage(): boolean {
  const user = userStore.user;
  if (!user) return false;
  // Stale persisted profiles predate is_welcome_page_eligible; treat them as
  // eligible.
  return user.is_welcome_page_eligible !== false;
}

function hasUserDismissedWelcomePage(): boolean {
  return userStore.user?.feature_flags?.welcome_page_dismissed === true;
}

export function isWelcomePageEnabled(): boolean {
  return (
    isWelcomePageEnabledByPostHog() &&
    isUserEligibleForWelcomePage() &&
    !hasUserDismissedWelcomePage()
  );
}

/** Default authenticated landing after login / onboarding completion. */
export function getPostAuthLandingRoute(): Routes.Welcome | Routes.Chats {
  return isWelcomePageEnabled() && isDefaultViewEnabledByPostHog()
    ? Routes.Welcome
    : Routes.Chats;
}

export function useWelcomePageEnabled(): boolean {
  // React Compiler memoizes hook returns, which would skip the MobX reads on
  // re-renders and break observer() tracking of userStore.
  'use no memo';
  return isWelcomePageEnabled();
}

/** Persists the dismissal server-side so it sticks across devices and reinstalls. */
export function dismissWelcomePage(): void {
  const user = userStore.user;
  if (!user) return;
  void userStore.syncUpdate({
    feature_flags: { ...user.feature_flags, welcome_page_dismissed: true },
  });
}
